/**
 * Preparses the custom block tags into the tags expected by the parser
 */
function preparseCustomBlockTags(html, tags) {
  return tags.reduce(preparseTag, html);
}

/**
 * Due to a limitation in the i-guide Editor, then custom block tags will
 * incorrectly be wrapped in tags, as seen in the example below.
 *
 * <p>[factbox]<p>
 * <p>Hej</p>
 * <p>[/factbox]</p>
 *
 * What we really want is the following:
 *
 * <factbox>
 * <p>Hej</p>
 * </factbox>
 */
function preparseTag(html = "", tag) {
  const tagsLeftToBeParsed = html.split(`[${tag}]`).length - 1;
  if (!tagsLeftToBeParsed) {
    return html;
  }
  const parsedOpeningTagHtml = parseOpeningTag(html, tag);
  const parsedClosingTagHtml = parseClosingTag(parsedOpeningTagHtml, tag);
  return preparseTag(parsedClosingTagHtml, tag);
}

/**
 * Helper that parses the opening [tag]
 */
function parseOpeningTag(html, tag) {
  const [before, ...remainder] = html.split(`[${tag}]`);
  const after = remainder.join(`[${tag}]`);
  if (!before) {
    return "";
  }
  let insertStartAt = before.length - 1;
  let encounteredTags = 0;

  // Move our 'cursor' backwards until we reach the root tag behind the found
  // custom tag.
  // Below is an example
  // ...</p><p><b>[factbox]</b></p>
  //     |
  while (insertStartAt > 0) {
    const currChar = before.charAt(insertStartAt);
    if (currChar === "<" && before.charAt(insertStartAt + 1) !== "/") {
      encounteredTags++;
    }
    if (currChar === "/" && before.charAt(insertStartAt - 1) === "<") {
      encounteredTags--;
      if (encounteredTags === 0) {
        break;
      }
    }
    insertStartAt--;
  }

  // Move cursor to the to be directly before our root tag
  while (insertStartAt < before.length) {
    const currChar = before.charAt(insertStartAt);
    if (currChar === ">") {
      insertStartAt++;
      break;
    }
    insertStartAt++;
  }

  // Extract content before and after the place to insert our tag
  const beforeInsertion = before.substring(0, insertStartAt);
  const afterInsertion = `${before.substring(insertStartAt)}${after}`;
  return `${beforeInsertion}<${tag}>${removeEmptyTagsAfterPlacementPoint(afterInsertion)}`;
}

/**
 * Helper that parses the closing [/tag]
 */
function parseClosingTag(html, tag) {
  const [start, ...endRemainder] = html.split(`[/${tag}]`);
  const end = endRemainder.join(`[/${tag}]`);
  let insertAt = 0;
  let encounteredTags = 0;

  // Move our 'cursor' forwards until we reach the root tag behind the found
  // custom tag.
  // Below is an example
  // <p><b>[factbox]</b></p><p>....
  //                        |
  while (insertAt < end.length) {
    const currChar = end.charAt(insertAt);
    if (currChar === "<" && end.charAt(insertAt + 1) !== "/" || insertAt === end.length) {
      encounteredTags++;
      if (encounteredTags === 0) {
        break;
      }
    }
    if (currChar === "/" && end.charAt(insertAt - 1) === "<") {
      encounteredTags--;
    }
    insertAt++;
  }

  // Extract content before and after the place to insert our tag
  const beforeInsertion = `${start}${end.substring(0, insertAt)}`;
  const afterInsertion = end.substring(insertAt);
  return `${removeEmptyTagsBeforePlacementPoint(beforeInsertion)}</${tag}>${afterInsertion}`;
}

/**
 * Removes unneeded tags placed after a [tag] has been moved.
 *
 * The search for tags looks to the right of the newly inserted <tag>
 */
function removeEmptyTagsAfterPlacementPoint(subHtml) {
  let index = 0;

  // In case there is no more content to the right, then bail out now
  if (subHtml.charAt(index) !== "<") {
    return subHtml;
  }
  let encounteredTags = 0;

  // Continue extracting tags until we have the whole substring that contained
  // the tag before
  while (index < subHtml.length) {
    if (subHtml.charAt(index) === "<" && subHtml.charAt(index + 1) !== "/") {
      encounteredTags--;
      if (encounteredTags === 0) {
        break;
      }
    }
    if (subHtml.charAt(index) === "<" && subHtml.charAt(index + 1) === "/") {
      encounteredTags++;
    }
    index++;
  }
  while (index < subHtml.length) {
    if (subHtml.charAt(index) === ">") {
      index++;
      break;
    }
    index++;
  }

  // In case there is no actual content inside the tags that contained the
  // tag before, then we can simply remove it.
  const hasNoContent = subHtml.substring(0, index).replace(/<(\/?)(.*?)>/g, "").replace(/\t|\n| |\u200B/g, "").length === 0;
  if (hasNoContent) {
    return subHtml.substring(index);
  } else {
    return subHtml;
  }
}

/**
 * Removes unneeded tags placed after a [tag] has been moved.
 *
 * The search for tags looks to the left of the newly inserted <tag>
 */
function removeEmptyTagsBeforePlacementPoint(subHtml) {
  let index = subHtml.length - 1;

  // In case there is no more content to the left, then bail out now
  if (subHtml.charAt(index) !== ">") {
    return subHtml;
  }
  let encounteredTags = 0;

  // Continue extracting tags until we have the whole substring that contained
  // the tag before
  while (index > 0) {
    if (subHtml.charAt(index) === "<" && subHtml.charAt(index + 1) !== "/") {
      encounteredTags--;
      if (encounteredTags === 0) {
        break;
      }
    }
    if (subHtml.charAt(index) === "<" && subHtml.charAt(index + 1) === "/") {
      encounteredTags++;
    }
    index--;
  }

  // In case there is no actual content inside the tags that contained the
  // tag before, then we can simply remove it.
  const hasNoContent = subHtml.substring(index).replace(/<(\/?)(.*?)>/g, "").replace(/\t|\n| |\u200B/g, "").length === 0;
  if (hasNoContent) {
    return subHtml.substring(0, index);
  } else {
    return subHtml;
  }
}

export { preparseCustomBlockTags };
//# sourceMappingURL=util.prepare-custom-block-tag.js.map
